/**
 * Created by Mikhail Menshenin on 09.07.2024
 */

const Village = function (level, options) {
    cleverapps.EventEmitter.call(this);

    options = options || {};

    this.location = options.location;
    this.player = options.player;

    const config = this.config = Village.getConfig(level);

    this.counter = new Counter();

    this.state = Village.STATE_BUILD;
    this.gold = 0;
    this.attention = 0;
    this.level = level;
    this.progress = 0;
    this.cost = Village.calculateVillageTotalCost(level);
    this.upgradeTool = false;

    this.name = config.name;
    this.huts = config.huts ? config.huts.map(function (hutData, index) {
        return new Hut(this, hutData, index);
    }, this) : [];

    this.crosses = cleverapps.rangeArray(0, 3).map(function (index) {
        return new Cross(this, index);
    }, this);

    this.load(options.data);

    this.checkAttention();
    this.huts.forEach((hut) => {
        hut.on("changeUpgradeable", this.checkAttention.bind(this));
    });

    if (cleverapps.config.debugMode) {
        this.checkHutExists();
    }
};

Village.prototype = Object.create(cleverapps.EventEmitter.prototype);
Village.prototype.constructor = Village;

Village.prototype.save = function () {
    if (this.location && this.location.village === this) {
        this.location.save();
    }
};

Village.prototype.getBundleId = function () {
    return `village_${this.level}`;
};

Village.prototype.openUpgradeWindow = function () {
    cleverapps.focusManager.display({
        focus: "VillageUpgradeWindow",
        control: ["MenuBarGoldItem"],
        actions: [
            function (f) {
                new VillageUpgradeWindow(this);
                cleverapps.focusManager.onceNoWindowsListener = f;
            }.bind(this),
            function (f) {
                f();
            }
        ]
    });
};

Village.prototype.changeUpgradeTools = function (isOpen) {
    this.upgradeTool = isOpen;
    this.huts.forEach((hut) => {
        hut.setUpgradeTool(isOpen);
    });
};

Village.prototype.getInfo = function () {
    const huts = [];
    this.huts.forEach((hut) => {
        const info = hut.getInfo();
        huts.push(info);
        return huts;
    });

    const crosses = this.crosses.map((cross) => cross.getInfo(), {});

    return {
        huts,
        crosses,
        raids: this.raids,
        gold: this.gold,
        goldCollected: this.goldCollected,
        level: this.level,
        state: this.state
    };
};

Village.prototype.load = function (data) {
    if (!data || !Object.keys(data).length) {
        return;
    }

    this.gold = data.gold || this.gold;
    this.goldCollected = data.goldCollected || this.goldCollected;
    this.state = data.state || this.state;
    this.level = data.level || this.level;

    if (data.huts && data.huts.length) {
        this.huts = data.huts.map(function (hutData, index) {
            return new Hut(this, hutData, index);
        }, this);

        this.huts.forEach((hut) => {
            hut.on("changeUpgradeable", this.checkAttention.bind(this));
        });
    }

    const hutNames = data.huts && Object.keys(data.huts) || [];
    hutNames.forEach((hutName) => {
        const hutData = data.huts[hutName];
        const hut = this.huts.find((hut) => hut.id === data.huts[hutName].id);
        if (hut) {
            hut.load(hutData);
        }
    });

    const crosses = data.crosses || [];
    crosses.forEach((cross, index) => {
        this.crosses[index].raided = cross.raided;
    });

    this.raids = data.raids || this.raids;
    this.progress = this.getCurrentProgress();
};

Village.prototype.getCurrentPlayer = function () {
    const playerInfo = cleverapps.user.getPlayerInfo();

    return {
        player: true,
        id: connector.platform.getUserID(),
        name: playerInfo.name,
        avatar: playerInfo.avatar
    };
};

Village.prototype.prepareToAttack = function () {
    this.state = Village.STATE_ATTACK;
};

Village.prototype.prepareToRaid = function () {
    this.gold = this.player.gold;
    this.state = Village.STATE_RAID;
    this.raids = Village.MAX_RAIDS;
    this.goldCollected = 0;
    const smallGold = Math.floor(this.gold / 1000 / 6) * 1000;
    const bigGold = this.gold - 2 * smallGold;
    const crossesGold = PseudoRandom.GET().shuffle([0, smallGold, smallGold, bigGold]);
    this.perfectRaid = true;

    this.crosses.forEach((cross, index) => {
        cross.raided = false;
        cross.gold = crossesGold[index];
    });
};

Village.prototype.getCurrentProgress = function () {
    return this.huts.reduce((progress, hut) => progress + hut.getCurrentLevel(), 0);
};

Village.prototype.getGoal = function () {
    return this.huts.reduce((goal, hut) => goal + hut.getMaxLevel(), 0);
};

Village.prototype.checkAttention = function () {
    const attention = this.huts.reduce((attention, hut) => {
        hut.checkUpgradeable();
        return attention + (hut.upgradeable ? 1 : 0);
    }, 0);

    if (this.attention !== attention) {
        this.attention = attention;
        this.trigger("changeAttention");
    }
};

Village.prototype.onHutClick = function (hut) {
    if (this.counter.isActive()) {
        return;
    }

    if (this.isStateBuild() && !hut.upgradeTool) {
        this.changeUpgradeTools(true);
        return;
    }

    if (hut.upgradeTool) {
        this.counter.inc();
        hut.upgrade(() => {
            this.counter.dec();
        });
        return;
    }

    if (this.canAttack() && hut.canAttack()) {
        this.attack(hut);
    }
};

Village.prototype.onHutDoubleClick = function (hut) {
    if (this.counter.isActive()) {
        return;
    }

    if (hut.isComplete()) {
        return;
    }

    if (this.isStateBuild()) {
        this.counter.inc();
        hut.upgrade(() => {
            this.counter.dec();
        });
    }
};

Village.prototype.attack = function (hut) {
    let p = Math.random();

    if (cleverapps.config.debugMode) {
        if (cleverapps.flags.get("villageAttackMode")) {
            if (cleverapps.flags.get("villageAttackMode") === "assault") {
                p = Village.DEFENSE_PROBABILITY + 1;
            } else {
                p = Village.DEFENSE_PROBABILITY - 1;
            }
            cleverapps.flags.set("villageAttackMode", undefined);
        }
    }

    if (p <= Village.DEFENSE_PROBABILITY) {
        this.defense();
    } else {
        this.assault(hut);
    }
};

Village.prototype.assault = function (hut) {
    this.state = Village.STATE_ASSAULT;
    const villageLevel = this.level;
    this.goldCollected = Village.GOLD_BY_ATTACK[villageLevel] || Village.GOLD_BY_ATTACK[Village.GOLD_BY_ATTACK.length - 1];

    hut.setDamage(hut.damage + 1, true);

    cleverapps.focusManager.display({
        focus: "VillageAttackAnimation",
        control: ["village"],
        actions: [
            function (f) {
                this.counter.inc();
                this.trigger("assault", f);
            }.bind(this),
            function (f) {
                hut.attack(f);
            },
            function (f) {
                this.counter.dec();
                f();
            }.bind(this)
        ]
    });
};

Village.prototype.defense = function () {
    this.state = Village.STATE_DEFENSE;
    const villageLevel = this.level;
    this.goldCollected = Village.GOLD_BY_DEFEND[villageLevel] || Village.GOLD_BY_DEFEND[Village.GOLD_BY_DEFEND.length - 1];

    cleverapps.focusManager.display({
        focus: "VillageDefenseAnimation",
        control: ["village"],
        actions: [
            function (f) {
                this.counter.inc();
                this.trigger("defense", f);
            }.bind(this),

            function (f) {
                this.counter.dec();
                f();
            }.bind(this)
        ]
    });
};

Village.prototype.isAttacked = function () {
    return this.state === Village.STATE_ASSAULT;
};

Village.prototype.onCrossClick = function (cross) {
    if (this.counter.isActive()) {
        return;
    }

    if (!cross.raided && this.canRaid()) {
        this.dig(cross);
    }
};

Village.prototype.dig = function (cross) {
    const gold = cross.gold;
    this.perfectRaid = this.perfectRaid && !!gold;

    cleverapps.focusManager.display({
        focus: "VillageDigAnimation",
        control: ["village"],
        actions: [
            function (f) {
                this.counter.inc();
                this.raids--;
                this.trigger("changeRaids");
                f();
            }.bind(this),
            function (f) {
                cross.dig(f);
            },
            function (f) {
                this.trigger("goldDigged", gold, f);
            }.bind(this),
            function (f) {
                if (gold) {
                    this.goldCollected += gold;
                    this.trigger("changeGoldCollected", f);
                    this.trigger("animateGoldHighlight");
                } else {
                    f();
                }
            }.bind(this),
            function (f) {
                this.counter.dec();
                f();
            }.bind(this)
        ]
    });
};

Village.prototype.updateProgress = function () {
    const progress = this.getCurrentProgress();

    if (this.progress !== progress) {
        this.progress = progress;

        this.save();

        this.trigger("changeProgress", this.checkGoal.bind(this));
    }
};

Village.prototype.checkGoal = function () {
    const goal = this.getGoal();

    if (this.progress >= goal) {
        Village.moveNextLocation();
    }
};

Village.prototype.isRaidFinished = function () {
    return !this.raids;
};

Village.prototype.isStateBuild = function () {
    return this.state === Village.STATE_BUILD;
};

Village.prototype.isStateAttack = function () {
    return this.state === Village.STATE_ATTACK;
};

Village.prototype.isStateRaid = function () {
    return this.state === Village.STATE_RAID;
};

Village.prototype.defended = function () {
    return this.state === Village.STATE_DEFENSE;
};

Village.prototype.checkHutExists = function () {
    const bundle = bundles[this.getBundleId()];
    const frames = cleverapps.unique(Object.keys(bundle.frames).map((frame) => frame.slice(0, frame.lastIndexOf("_"))));
    const jsons = Object.keys(bundle.jsons).filter((json) => json.indexOf("vertical_") === 0).map((json) => json.slice(9));
    const existHuts = this.huts.filter((hut) => frames.indexOf(hut.name) !== -1 || jsons.indexOf(hut.name) !== -1);
    const existHutNames = existHuts.map((hut) => hut.name);
    const addHuts = cleverapps.unique(cleverapps.substract(frames.concat(jsons), existHutNames));
    this.huts = existHuts.concat(addHuts.map((name, index) => new Hut(this, { name }, index)));
    this.huts.forEach((hut, index) => {
        hut.id = index;
    });
};

Village.prototype.canAttack = function () {
    return this.isStateAttack();
};

Village.prototype.canRaid = function () {
    return this.isStateRaid() && this.raids > 0;
};

Village.prototype.receiveGold = function () {
    const reward = {
        hard: this.goldCollected
    };
    const event = this.isStateRaid() ? cleverapps.EVENTS.EARN.VILLAGE_RAID : cleverapps.EVENTS.EARN.VILLAGE_ATTACK;
    const rewardList = new RewardsList(reward, { event });
    rewardList.receiveRewards();
    rewardList.onAnimationFinished();
};

if (cleverapps.config.debugMode) {
    Village.prototype.attackSomeone = function () {
        const hut = PseudoRandom.GET().choose(this.huts.filter((hut) => hut.canAttack()));

        this.attack(hut);
    };
}

Village.calculateVillageTotalCost = function (levelNo) {
    if (levelNo < 14) {
        return (3900000 + levelNo * 4500000) * 7 / 50;
    }

    levelNo += 1;
    const price = -37100000 + 17400000 * levelNo - 1140000 * Math.pow(levelNo, 2) + 35467 * Math.pow(levelNo, 3) - 466 * Math.pow(levelNo, 4) + 2.87 * Math.pow(levelNo, 5) - 0.00434 * Math.pow(levelNo, 6);
    return price * 7 / 50;
};

Village.MAX_RAIDS = 3;

Village.STATE_BUILD = 1;
Village.STATE_ATTACK = 2;
Village.STATE_ASSAULT = 21;
Village.STATE_DEFENSE = 22;
Village.STATE_RAID = 3;

Village.DEFENSE_PROBABILITY = 0.5;

Village.GOLD_BY_ATTACK = [
    28000, 28000, 35000, 42000, 49000, 56000, 63000, 70000, 77000, 84000,
    91000, 91000, 98000, 98000, 105000, 112000, 115500, 115500, 115500, 119000,
    119000, 119000, 119000, 119000, 122500, 122500, 122500, 122500, 122500, 126000,
    126000, 126000, 126000, 126000, 133000, 133000, 133000, 133000, 133000, 140000,
    140000, 140000, 140000, 140000, 140000, 154000, 154000, 154000, 154000, 168000,
    168000, 168000, 168000, 168000, 168000, 168000, 168000, 168000, 168000, 182000,
    182000, 182000, 182000, 182000, 182000, 182000, 182000, 182000, 182000, 189000,
    189000, 189000, 189000, 189000, 189000, 189000, 189000, 189000, 189000, 189000,
    189000, 189000, 189000, 189000, 196000, 196000, 196000, 196000, 196000, 196000,
    196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000,
    196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000,
    196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000, 196000,
    203000, 203000, 203000, 203000, 203000, 203000, 203000, 203000, 203000, 203000,
    203000, 203000, 203000, 203000, 210000, 210000, 210000, 210000, 210000, 210000,
    210000, 210000, 210000, 210000, 210000, 210000, 210000, 210000, 210000, 504000,
    504000, 504000, 504000, 504000, 560000, 560000, 560000, 560000, 644000, 644000,
    644000, 644000, 644000, 644000, 756000, 756000, 756000, 756000, 756000, 840000,
    840000, 840000, 840000, 840000, 980000, 980000, 980000, 980000, 980000, 1120000,
    1120000, 1120000, 1120000, 1120000, 1260000, 1260000, 1260000, 1260000, 1260000, 1400000,
    1400000, 1400000, 1400000, 1400000, 1400000, 1400000, 1400000, 1400000, 1400000, 1400000];

Village.GOLD_BY_DEFEND = [
    7000, 7000, 10500, 14000, 14000, 14000, 14000, 21000, 21000, 28000,
    28000, 28000, 28000, 28000, 28000, 28000, 31500, 31500, 31500, 35000,
    35000, 35000, 35000, 35000, 35000, 35000, 35000, 35000, 35000, 35000,
    38500, 38500, 38500, 38500, 38500, 38500, 38500, 38500, 38500, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000, 42000,
    42000, 42000, 42000, 42000, 49000, 49000, 49000, 49000, 49000, 56000,
    56000, 56000, 56000, 56000, 70000, 70000, 70000, 70000, 70000, 140000,
    140000, 140000, 140000, 140000, 140000, 140000, 140000, 140000, 140000, 140000,
    140000, 140000, 140000, 140000, 280000, 280000, 280000, 280000, 280000, 280000,
    280000, 280000, 280000, 280000, 280000, 280000, 280000, 280000, 280000, 420000,
    420000, 420000, 420000, 420000, 420000, 420000, 420000, 420000, 420000, 420000,
    420000, 420000, 420000, 420000, 420000, 420000, 420000, 420000, 420000, 420000
];

Village.prototype.getProgress = function () {
    return {
        current: this.getCurrentProgress(),
        goal: this.getGoal()
    };
};

Village.prototype.getConfig = function () {
    return this.config;
};

Village.generate = function (config) {
    const data = {};

    data.huts = [];
    config.huts.forEach((hut) => {
        const level = PseudoRandom.GET().random(0, Hut.MAX_LEVEL);

        const dataHut = {
            level,
            name: hut.name
        };
        if (level) {
            const damage = PseudoRandom.GET().random(0, level - 1);
            if (damage) {
                dataHut.damage = damage;
            }
        }
        data.huts.push(dataHut);
    });

    return data;
};

Village.prototype.getBackgroundBundle = function () {
    return (cleverapps.resolution.mode === cleverapps.WideMode.VERTICAL ? "village_background_vertical_" : "village_background_horizontal_") + this.level;
};

Village.prototype.getPlayer = function () {
    return this.player || this.getCurrentPlayer();
};

Village.prototype.isFirstInit = function () {
    return Boolean(this.location) && this.level === 0 && this.progress === 0;
};

Village.prototype.isFirstUpgrade = function () {
    return Boolean(this.location) && this.level === 0 && this.progress === 1;
};

Village.prototype.upgradeHut = function (hut, price, cb) {
    cleverapps.focusManager.displayWhenFreeFocus({
        focus: "VillageHutUgrade",
        control: ["MenuBarGoldItem", "village", "progress"],
        actions: [
            function (f) {
                hut.trigger("upgrade", price, f);
            },
            function (f) {
                if (this.isFirstUpgrade()) {
                    const sceneComponent = VillageSceneComponent.GetRunning();
                    cleverapps.forces.create(sceneComponent.progressControl, {
                        pointer: true,
                        finger: false,
                        closeByTouchInShadow: true,
                        text: "Tutorial.CoinKingdom.FirstUpgradeForce"
                    }, {
                        highlights: [sceneComponent.villageView]
                    });
                    cleverapps.forces.onceForceClosed = f;
                } else {
                    f();
                }
            }.bind(this),
            function (f) {
                if (this.isFirstUpgrade()) {
                    const scene = cleverapps.Scenes.GetRunningScene();
                    cleverapps.focusManager.showControlsWhileFocused("SlotMachineIcon", true);
                    cleverapps.forces.create(cleverapps.aims.getTarget("SlotMachineIcon"), {
                        text: "Tutorial.CoinKingdom.SlotMachineIconForce"
                    }, {
                        actives: [cleverapps.aims.getTarget("SlotMachineIcon"), ComponentUtils.findChildByName(scene, "upMenu")]
                    });
                    cleverapps.forces.onceForceClosed = f;
                } else {
                    f();
                }
            }.bind(this),
            function (f) {
                cb();
                f();
            }
        ]
    });
};

Village.getMaxLevel = function () {
    return Village.getConfigs().length - 1;
};

Village.canMoveNext = function () {
    return cleverapps.meta.getSelectedLocation().village.level < this.getMaxLevel();
};

Village.moveNextLocation = function (reset) {
    if (!this.canMoveNext()) {
        cleverapps.focusManager.display({
            focus: "NextVillageScene",
            control: [],
            action: function (f) {
                new AllDoneWindow();
                cleverapps.focusManager.onceNoWindowsListener = f;
            }
        });
        return;
    }

    cleverapps.focusManager.display({
        focus: "NextVillageScene",
        control: [],
        actions: [
            function (f) {
                const location = cleverapps.meta.getSelectedLocation();
                if (reset) {
                    location.reset();
                } else {
                    location.moveNextVillage();
                }
                f();
            },
            function (f) {
                cleverapps.meta.gotoLocationScene(f);
            }
        ]
    });
};

Village.getConfig = function (level) {
    return Village.getConfigs()[level];
};

Village.getConfigs = function () {
    if (Village.configs) {
        return Village.configs;
    }

    const configs = [];
    for (let id = 0; bundles[`village_background_horizontal_${id}`] && bundles[`village_background_vertical_${id}`]; id++) {
        const huts = {};
        const bundle = bundles[`village_${id}`];
        if (bundle) {
            Object.keys(bundle.frames).forEach((frame) => {
                const hut = frame.slice(0, frame.lastIndexOf("_"));
                const level = parseInt(frame.slice(hut.length + 1));
                if (!huts[hut] || huts[hut].maxLevel < level) {
                    huts[hut] = {
                        name: hut,
                        maxLevel: level
                    };
                }
            });
        }

        configs.push({
            level: id,
            name: `village_${id}`,
            huts: Object.keys(huts).sort().map((hut) => huts[hut])
        });
    }

    Village.configs = configs;
    return configs;
};

Village.CreateEnemy = function (player) {
    const config = Village.getConfig(player.villageLevel);
    return new Village(player.villageLevel, { player, data: Village.generate(config) });
};